import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { StarRatingDisplay, TimeSeriesLineChart, type ChartSeries, type TimePoint } from '@org/ui';
import { useAnalytics } from '../analytics-context';
import { ReportCard } from '../report-card';
import { ReportTable } from '../report-table';
import { TimeSeriesCard } from '../time-series-card';
import { DeltaText } from '../delta-text';
import { formatBucket, formatDate, formatRate } from '../format';

export function AttendanceReports() {
  const { t, i18n } = useTranslation('admin');
  const { input, compareInput, grain, search } = useAnalytics();
  const comparing = Boolean(compareInput);
  const classes = trpc.analytics.attendance.classesOverTime.useQuery(input);
  const cmpClasses = trpc.analytics.attendance.classesOverTime.useQuery(compareInput ?? input, { enabled: comparing });
  const rate = trpc.analytics.attendance.attendanceRate.useQuery(input);
  const cmpRate = trpc.analytics.attendance.attendanceRate.useQuery(compareInput ?? input, { enabled: comparing });
  const scorecard = trpc.analytics.teachers.scorecard.useQuery(input);
  const cmpScorecard = trpc.analytics.teachers.scorecard.useQuery(compareInput ?? input, { enabled: comparing });
  const groupTrend = trpc.analytics.teachers.attendanceByGroupTrend.useQuery(input);
  const byGroup = trpc.analytics.attendance.byGroup.useQuery(input);
  const byStudent = trpc.analytics.attendance.byStudent.useQuery(input);
  const teachers = trpc.analytics.attendance.teacherLoad.useQuery(input);

  const lang = i18n.language;
  const xLabel = (b: string) => formatBucket(b, grain, lang);
  const kindSeries: ChartSeries[] = [
    { key: 'group-class', label: t('common.eventKinds.group-class') },
    { key: 'private-class', label: t('common.eventKinds.private-class') },
    { key: 'tutoring', label: t('common.eventKinds.tutoring') },
    { key: 'closed-event', label: t('common.eventKinds.closed-event') },
    { key: 'open-event', label: t('common.eventKinds.open-event') },
    { key: 'cancelled', label: t('analytics.attendance.classes.cancelled') },
  ];
  const rateSeries: ChartSeries[] = [{ key: 'rate', label: t('analytics.attendance.rate.series') }];
  const toRateSeries = (rows: { bucket: string; rate: number | null }[] | undefined): TimePoint[] =>
    (rows ?? []).map((p) => {
      const point: TimePoint = { bucket: p.bucket };
      if (p.rate !== null) point.rate = p.rate;
      return point;
    });
  const rateData = toRateSeries(rate.data);
  const cmpRateData = toRateSeries(cmpRate.data);
  const pctFormat = (n: number) => formatRate(n);
  const cmpByTeacher = new Map((comparing ? cmpScorecard.data ?? [] : []).map((r) => [r.teacherId, r]));
  const delta = (teacherId: string, pick: (r: NonNullable<typeof scorecard.data>[number]) => number | null, current: number | null) => {
    const other = cmpByTeacher.get(teacherId);
    if (!other || current === null) return null;
    const prev = pick(other);
    return prev === null ? null : current - prev;
  };
  const cmpLabel = t(search.compare === 'lastYear' ? 'analytics.compare.lastYearShort' : 'analytics.compare.previousShort');
  const overallSeries: ChartSeries[] = [
    { key: 'rate', label: t('analytics.attendance.smallMultiples.group') },
    { key: 'overall', label: t('analytics.attendance.smallMultiples.overall'), color: '#9a9994', dashed: true },
  ];

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-2">
        <TimeSeriesCard
          title={t('analytics.attendance.classes.title')}
          description={t('analytics.attendance.classes.description')}
          data={classes.data}
          cmpData={cmpClasses.data}
          series={kindSeries}
          stacked
          isLoading={classes.isLoading}
          error={classes.error}
        />

        <TimeSeriesCard
          title={t('analytics.attendance.rate.title')}
          description={t('analytics.attendance.rate.description')}
          data={rateData}
          cmpData={comparing ? cmpRateData : undefined}
          series={rateSeries}
          kind="line"
          yDomain={[0, 100]}
          valueFormat={pctFormat}
          isLoading={rate.isLoading}
          error={rate.error}
        />
      </div>

      <ReportCard
        title={t('analytics.attendance.scorecard.title')}
        description={t('analytics.attendance.scorecard.description')}
        isLoading={scorecard.isLoading}
        error={scorecard.error}
        isEmpty={scorecard.data?.length === 0}
      >
        <ReportTable
          rows={scorecard.data ?? []}
          rowKey={(r) => r.teacherId}
          defaultSort="students"
          columns={[
            { key: 'name', label: t('analytics.common.teacher'), value: (r) => r.name, className: 'font-medium' },
            { key: 'students', label: t('analytics.attendance.scorecard.students'), value: (r) => r.students, align: 'right' },
            { key: 'classes', label: t('analytics.attendance.teachers.classes'), value: (r) => r.classes, align: 'right' },
            {
              key: 'attendance',
              label: t('analytics.attendance.rateLabel'),
              value: (r) => r.attendanceRate,
              align: 'right',
              render: (r) => (
                <>
                  {formatRate(r.attendanceRate)}
                  <DeltaText value={delta(r.teacherId, (o) => o.attendanceRate, r.attendanceRate)} />
                </>
              ),
            },
            {
              key: 'homework',
              label: t('analytics.attendance.scorecard.homework'),
              value: (r) => r.homeworkRate,
              align: 'right',
              render: (r) => (
                <>
                  {formatRate(r.homeworkRate)}
                  <DeltaText value={delta(r.teacherId, (o) => o.homeworkRate, r.homeworkRate)} />
                </>
              ),
            },
            {
              key: 'app',
              label: t('analytics.attendance.scorecard.app'),
              value: (r) => r.appActiveRate,
              align: 'right',
              render: (r) => (
                <>
                  {formatRate(r.appActiveRate)}
                  <DeltaText value={delta(r.teacherId, (o) => o.appActiveRate, r.appActiveRate)} />
                </>
              ),
            },
            {
              key: 'rating',
              label: t('analytics.attendance.scorecard.rating'),
              value: (r) => r.avgRating,
              render: (r) => (r.avgRating === null ? '—' : <StarRatingDisplay value={r.avgRating} size={13} />),
            },
            {
              key: 'retention',
              label: t('analytics.attendance.scorecard.retention'),
              value: (r) => r.retentionRate,
              align: 'right',
              render: (r) => (
                <>
                  {formatRate(r.retentionRate)}
                  <DeltaText value={delta(r.teacherId, (o) => o.retentionRate, r.retentionRate)} />
                </>
              ),
            },
          ]}
        />
        <p className="mt-2 text-xs text-muted-foreground">
          {comparing ? t('analytics.attendance.scorecard.deltaHint', { period: cmpLabel }) : t('analytics.attendance.scorecard.hint')}
        </p>
      </ReportCard>

      <ReportCard
        title={t('analytics.attendance.smallMultiples.title')}
        description={t('analytics.attendance.smallMultiples.description')}
        isLoading={groupTrend.isLoading}
        error={groupTrend.error}
        isEmpty={groupTrend.data?.groups.length === 0}
      >
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {(groupTrend.data?.groups ?? []).slice(0, 24).map((g) => {
            const data: TimePoint[] = g.series.map((p, i) => {
              const point: TimePoint = { bucket: p.bucket };
              if (p.rate !== null) point.rate = p.rate;
              const overall = groupTrend.data?.overall[i]?.rate;
              if (overall !== null && overall !== undefined) point.overall = overall;
              return point;
            });
            return (
              <div key={g.groupId} className="rounded-lg border p-3">
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium" title={g.name ?? ''}>{g.name}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {[g.levelName, g.teacherName].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <div className="shrink-0 text-sm font-semibold tabular-nums">{formatRate(g.avgRate)}</div>
                </div>
                <TimeSeriesLineChart
                  data={data}
                  series={overallSeries}
                  height={120}
                  yDomain={[0, 100]}
                  valueFormat={pctFormat}
                  xLabelFormat={xLabel}
                  emptyLabel={t('analytics.common.noData')}
                  className="[&_[data-testid=chart-legend]]:hidden"
                />
              </div>
            );
          })}
        </div>
        <p className="mt-3 text-xs text-muted-foreground">{t('analytics.attendance.smallMultiples.hint')}</p>
      </ReportCard>

      <ReportCard
        title={t('analytics.attendance.byGroup.title')}
        description={t('analytics.attendance.byGroup.description')}
        isLoading={byGroup.isLoading}
        error={byGroup.error}
        isEmpty={byGroup.data?.length === 0}
      >
        <ReportTable
          rows={byGroup.data ?? []}
          rowKey={(r) => r.groupId}
          defaultSort="rate"
          searchKeys={['group', 'teacher']}
          columns={[
            { key: 'group', label: t('analytics.attendance.byGroup.group'), value: (r) => r.groupName },
            { key: 'level', label: t('analytics.common.level'), value: (r) => r.levelName },
            { key: 'teacher', label: t('analytics.common.teacher'), value: (r) => r.teacherName },
            { key: 'members', label: t('analytics.attendance.byGroup.members'), value: (r) => r.members, align: 'right' },
            { key: 'sessions', label: t('analytics.attendance.rate.sessions'), value: (r) => r.sessions, align: 'right' },
            { key: 'present', label: t('analytics.attendance.present'), value: (r) => r.present, align: 'right' },
            { key: 'total', label: t('analytics.attendance.seats'), value: (r) => r.total, align: 'right' },
            { key: 'rate', label: t('analytics.attendance.rateLabel'), value: (r) => r.rate, render: (r) => formatRate(r.rate), align: 'right' },
          ]}
        />
      </ReportCard>

      <ReportCard
        title={t('analytics.attendance.byStudent.title')}
        description={t('analytics.attendance.byStudent.description')}
        isLoading={byStudent.isLoading}
        error={byStudent.error}
        isEmpty={byStudent.data?.length === 0}
      >
        <ReportTable
          rows={byStudent.data ?? []}
          rowKey={(r) => r.userId}
          defaultSort="rate"
          defaultDirection="asc"
          searchKeys={['name', 'email']}
          columns={[
            { key: 'name', label: t('analytics.common.student'), value: (r) => r.name },
            { key: 'email', label: t('analytics.common.email'), value: (r) => r.email, className: 'text-muted-foreground' },
            { key: 'level', label: t('analytics.common.level'), value: (r) => r.levelName },
            { key: 'present', label: t('analytics.attendance.present'), value: (r) => r.present, align: 'right' },
            { key: 'total', label: t('analytics.attendance.byStudent.classes'), value: (r) => r.total, align: 'right' },
            { key: 'rate', label: t('analytics.attendance.rateLabel'), value: (r) => r.rate, render: (r) => formatRate(r.rate), align: 'right' },
            {
              key: 'last',
              label: t('analytics.retention.recency.lastPresent'),
              value: (r) => r.lastPresentAt,
              render: (r) => formatDate(r.lastPresentAt, lang),
            },
          ]}
        />
      </ReportCard>

      <ReportCard
        title={t('analytics.attendance.teachers.title')}
        description={t('analytics.attendance.teachers.description')}
        isLoading={teachers.isLoading}
        error={teachers.error}
        isEmpty={teachers.data?.length === 0}
      >
        <ReportTable
          rows={teachers.data ?? []}
          rowKey={(r) => r.teacherId}
          defaultSort="classes"
          columns={[
            { key: 'name', label: t('analytics.common.teacher'), value: (r) => r.name },
            { key: 'groups', label: t('analytics.attendance.teachers.groups'), value: (r) => r.groups, align: 'right' },
            { key: 'classes', label: t('analytics.attendance.teachers.classes'), value: (r) => r.classes, align: 'right' },
            { key: 'scorecards', label: t('analytics.attendance.teachers.scorecards'), value: (r) => r.scorecards, align: 'right' },
            { key: 'pending', label: t('analytics.attendance.teachers.pending'), value: (r) => r.pendingScorecards, align: 'right' },
            { key: 'rate', label: t('analytics.attendance.rateLabel'), value: (r) => r.attendanceRate, render: (r) => formatRate(r.attendanceRate), align: 'right' },
          ]}
        />
      </ReportCard>
    </div>
  );
}
